/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';
import * as Yup from 'yup';
import { PaymentUpdate, RenderedOptions } from '../types';
import SelectOptionLocked from '../../../../UI/Select/SelectOptionLocked';

export type BuildPaymentUpdateRowsProps = {
  country: string;
  financialInstitutions: RenderedOptions[];
  branchTransitNumbers: RenderedOptions[];
  type: 'Payable' | 'Receivable';
  updateType: 'Add' | 'Edit';
  originalValue?: vp.TemporaryBillsOrInvoicesRails;
};
export type PaymentUpdateRows = {
  label?: vp.PaymentUpdateLabel;
  id?: vp.PaymentUpdateId;
  require: (ctx: PaymentUpdate) => boolean;
  visible: (ctx: PaymentUpdate) => boolean;
  validate?: (ctx: PaymentUpdate) => unknown;
  isDisabled?: boolean;
  preset?: keyof vp.TextFieldPresets | undefined;
  type?: 'input' | 'select' | 'selectFinanacialInstitution' | 'googleAddress' | 'radio' | 'date';
  placeholder?: string;
  getSelectOptions?: (ctx: PaymentUpdate) => RenderedOptions[];
  getRadioOptions?: (ctx: PaymentUpdate) => Record<string, string>;
  renderOption?: (o: RenderedOptions, args: vp.SelectRenderOptionArgs) => React.ReactNode;
  container: 'field' | 'wrapper';
  showValue?: boolean;
  paymentRows: PaymentUpdateRows[];
  filterable?: boolean;
};

export const buildPaymentUpdateRows = ({
  type,
  financialInstitutions,
  branchTransitNumbers,
  country,
  updateType,
  originalValue,
}: BuildPaymentUpdateRowsProps): PaymentUpdateRows[] => {
  const InteracMethodTypes: RenderedOptions[] = [
    { label: 'Email Address', value: 'Email Address' },
    { label: 'Phone Number', value: 'Phone Number' },
  ];
  const paymentsDropdown = Object.entries(
    getPaymentMethodsNeededDropdown(
      originalValue || { hasBankAccount: false, hasCreditCard: false, hasDebitCard: false, hasInterac: false },
      country,
      type,
    ),
  ).map((entry) => ({ label: entry[0], value: entry[0], renderOption: entry[1] }));
  return [
    {
      label: vp.PaymentUpdateLabel.PaymentMethod,
      id: vp.PaymentUpdateId.PaymentMethod,
      require: () => true,
      visible: () => true,
      container: 'field',
      type: updateType === 'Edit' ? 'input' : 'select',
      placeholder: '',
      preset: undefined,
      isDisabled: updateType === 'Edit' || Object.values(paymentsDropdown).length <= 1,
      getSelectOptions: () => paymentsDropdown,
      paymentRows: [],
      renderOption: (o, args) => (
        <SelectOptionLocked {...args} featureKey={o.value ? o.value : ''}>
          {o.renderOption || o.label}
        </SelectOptionLocked>
      ),
      validate: () => Yup.string().required('This value is required.'),
    },
    {
      label: vp.PaymentUpdateLabel.FinancialInstitutionNumber,
      id: vp.PaymentUpdateId.FinancialInstitutionNumber,
      require: () => true,
      visible: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'CA',
      type: 'select',
      placeholder: '',
      preset: undefined,
      showValue: true,
      getSelectOptions: () => financialInstitutions,
      renderOption: (o, { Option }) => <Option>{o.renderOption || o.label}</Option>,
      container: 'field',
      paymentRows: [],
      filterable: true,
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'CA'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.BankTransitNumber,
      id: vp.PaymentUpdateId.BankTransitNumber,
      require: () => true,
      showValue: true,
      visible: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'CA',
      type: 'select',
      placeholder: '',
      container: 'field',
      paymentRows: [],
      preset: undefined,
      filterable: true,
      getSelectOptions: () => branchTransitNumbers,
      renderOption: (o, { Option }) => <Option>{o.renderOption || o.label}</Option>,
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'CA'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.RoutingNumber,
      id: vp.PaymentUpdateId.RoutingNumber,
      require: () => true,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'input',
      placeholder: '',
      preset: 'number',
      container: 'field',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.AccountNumber,
      id: vp.PaymentUpdateId.AccountNumber,
      require: () => true,
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant' || ctx.PaymentMethod === 'RTP',
      type: 'input',
      placeholder: '',
      preset: 'number',
      container: 'field',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Address,
      id: vp.PaymentUpdateId.Address,
      require: () => true,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'googleAddress',
      container: 'field',
      placeholder: '',
      preset: undefined,
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.City,
      id: vp.PaymentUpdateId.City,
      require: () => true,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'input',
      placeholder: '',
      preset: 'text',
      container: 'field',
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.State,
      id: vp.PaymentUpdateId.State,
      require: () => true,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'input',
      placeholder: '',
      container: 'field',
      paymentRows: [],
      preset: 'text',
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'US'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.ZipCode,
      id: vp.PaymentUpdateId.ZipCode,
      require: () => true,
      showValue: true,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'input',
      placeholder: '',
      container: 'field',
      paymentRows: [],
      preset: 'text',
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.IdentificationNumber,
      id: vp.PaymentUpdateId.IdentificationNumber,
      require: () => true,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'input',
      container: 'field',
      placeholder: '',
      preset: 'tel',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.PhoneNumber,
      id: vp.PaymentUpdateId.PhoneNumber,
      require: () => true,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'input',
      container: 'field',
      placeholder: '',
      preset: 'tel',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.InteracMethod,
      id: vp.PaymentUpdateId.InteracMethod,
      require: () => true,
      visible: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      type: 'select',
      container: 'field',
      placeholder: '',
      preset: 'text',
      getSelectOptions: () => InteracMethodTypes,
      paymentRows: [],
      validate: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Email,
      id: vp.PaymentUpdateId.Email,
      require: () => true,
      visible: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        ctx.InteracMethod === 'Email Address',
      type: 'input',
      container: 'field',
      placeholder: 'Provide email address',
      preset: 'email',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        ctx.InteracMethod === 'Email Address'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Phone,
      id: vp.PaymentUpdateId.Phone,
      require: () => true,
      visible: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        ctx.InteracMethod === 'Phone Number',
      type: 'input',
      container: 'field',
      placeholder: 'Provide phone number',
      preset: 'tel',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        ctx.InteracMethod === 'Phone Number'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Question,
      id: vp.PaymentUpdateId.Question,
      require: () => type === 'Payable',
      visible: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      type: 'input',
      container: 'field',
      placeholder: 'Provide security question',
      preset: 'text',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        type === 'Payable'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Answer,
      id: vp.PaymentUpdateId.Answer,
      require: () => type === 'Payable',
      visible: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      type: 'input',
      container: 'field',
      placeholder: 'Provide security answer',
      preset: 'text',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        type === 'Payable'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Language,
      id: vp.PaymentUpdateId.Language,
      require: () => true,
      visible: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      type: 'radio',
      container: 'field',
      placeholder: '',
      preset: 'text',
      getRadioOptions: () => ({
        en: 'English',
        fr: 'French',
      }),
      paymentRows: [],
      validate: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request'
          ? Yup.string().required('This value is required.')
          : Yup.string().notRequired(),
    },
  ];
};
export function getPaymentMethodsNeededDropdown(
  options: { hasBankAccount: boolean; hasCreditCard: boolean; hasDebitCard: boolean; hasInterac: boolean },
  country: string,
  PaymentMethod: 'Payable' | 'Receivable',
): { [key: string]: string } {
  let newObject: { [key: string]: string } = {};
  if (!options.hasBankAccount) {
    if (country === 'CA' && PaymentMethod === 'Receivable') {
      newObject = {
        ...newObject,
        'Bank Account': 'Bank Account',
      };
    }
    if (country === 'CA' && PaymentMethod === 'Payable') {
      newObject = {
        ...newObject,
        'Bank Account': 'Bank Account',
        'VoPay Instant': 'VoPay Instant',
      };
    }
    if (country === 'US' && PaymentMethod === 'Receivable') {
      newObject = {
        ...newObject,
        'Bank Account': 'Bank Account',
      };
    }
    if (country === 'US' && PaymentMethod === 'Payable') {
      newObject = {
        ...newObject,
        'Bank Account': 'Bank Account',
        RTP: 'RTP',
      };
    }
  }
  if (!options.hasCreditCard) {
    if (PaymentMethod === 'Receivable') {
      newObject = {
        ...newObject,
        'Credit Card': 'Credit Card',
      };
    }
  }
  if (!options.hasDebitCard) {
    newObject = {
      ...newObject,
      'Debit Card': 'Debit Card',
    };
  }
  if (!options.hasInterac) {
    if (country === 'CA' && PaymentMethod === 'Receivable') {
      newObject = {
        ...newObject,
        'Interac e-Transfer Money Request': 'Interac e-Transfer Money Request',
      };
    }
    if (country === 'CA' && PaymentMethod === 'Payable') {
      newObject = {
        ...newObject,
        'Interac e-Transfer': 'Interac e-Transfer',
      };
    }
  }
  return newObject;
}
